import { z } from 'zod';
import { zGameCode, zGameStatus, zTimingProfile, zTurnPhase } from './enums.js';
import { zSeatSummary, zSideBrandingSet } from './session.js';
/**
 * Game lifecycle and the turn state machine (§8.1, §8.2).
 *
 * The single most important thing in this file is that **every deadline is an
 * absolute epoch millisecond on the server clock** (§8.2), never a duration.
 * Clients estimate their offset from the ping/pong round trip and render
 * countdowns against the corrected clock. That is the difference between everyone's
 * timer agreeing and a support ticket every session.
 *
 * `PAUSED` is the exception that proves it: a frozen timer has no deadline, so the
 * server sends `deadlineAtMs: null` and `remainingMs` instead, and the client
 * renders "paused" as a distinct state rather than as a number that has stopped
 * moving — which is indistinguishable from a countdown that has broken.
 */
const strict = (shape) => z.object(shape).strict();
/**
 * Per-game toggles, stored in `games.config`. Validated on write so the JSONB
 * column is a schema rather than a junk drawer.
 */
export const zGameConfig = strict({
    /** §6.2 — does CREWCOM see the team's draft orders? */
    crewcomSeesDrafts: z.boolean().default(true),
    /** §3.2 — may one person hold several seats? Expected in undermanned games. */
    multiSeating: z.boolean().default(true),
    /** §12 — live objective progress helps training, hiding it helps competition. */
    showObjectiveProgress: z.boolean().default(true),
});
export const DEFAULT_GAME_CONFIG = zGameConfig.parse({});
export const zCreateGameRequest = strict({
    scenario: z.string().min(1).max(120),
    timingProfile: zTimingProfile.optional(),
    maxTurns: z.number().int().min(1).max(60).optional(),
    config: zGameConfig.partial().optional(),
    /**
     * Optional override for the RNG root (§7.4). Supplying it makes a game exactly
     * reproducible, which is what an instructor wants when re-running a scenario for
     * a second cohort. Omitted, the server generates one — and either way it never
     * leaves the server.
     */
    gameSeed: z.string().min(8).max(128).optional(),
});
/**
 * The passwords are returned **once**, at creation, and never again — they are
 * stored argon2id-hashed (§3.1). Losing them means rotating them, not recovering
 * them.
 */
export const zCreateGameResponse = strict({
    gameId: z.string().uuid(),
    code: zGameCode,
    scenario: z.string(),
    passwords: strict({
        blufor: z.string(),
        redfor: z.string(),
        gm: z.string(),
    }).readonly(),
}).readonly();
export const zPhaseState = strict({
    status: zGameStatus,
    turn: z.number().int(),
    maxTurns: z.number().int(),
    phase: zTurnPhase,
    /** Absolute, on the server clock. Null while PAUSED, in LOBBY, or COMPLETE. */
    deadlineAtMs: z.number().int().nullable(),
    /** Only set while PAUSED — what was left on the clock when it stopped (§4.10). */
    remainingMs: z.number().int().nullable(),
    serverTimeMs: z.number().int(),
}).readonly();
export const zGameSummary = strict({
    gameId: z.string().uuid(),
    code: zGameCode,
    scenario: z.string(),
    /** What this scenario calls its three sides — resolved, never partial. */
    sides: zSideBrandingSet,
    timingProfile: zTimingProfile,
    phase: zPhaseState,
    config: zGameConfig,
    seats: z.array(zSeatSummary).readonly(),
    contentVersion: z.string(),
    contentHash: z.string(),
}).readonly();
// ---------------------------------------------------------------------------
// GM controls (§11.1's timer control — the rest of the console is M6)
// ---------------------------------------------------------------------------
export const zGmAction = z.enum([
    'START',
    'PAUSE',
    'RESUME',
    'EXTEND',
    'FORCE_CLOSE',
    'ADVANCE_PHASE',
    'ABANDON',
]);
export const zGmControlRequest = strict({
    action: zGmAction,
    /** EXTEND only: milliseconds to add to the live deadline. */
    extendMs: z.number().int().min(1_000).max(3_600_000).optional(),
    reason: z.string().trim().max(200).optional(),
});
/** A GM broadcast or inject (§8.4 `gm.message`). Free text in M2; structured in M6. */
export const zGmMessagePayload = strict({
    from: z.literal('GM'),
    text: z.string().max(2000),
    atMs: z.number().int(),
    severity: z.enum(['INFO', 'WARNING', 'CRITICAL']),
}).readonly();
//# sourceMappingURL=game.js.map